import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  CardHeader,
  Collapse
} from 'reactstrap';
import Select from 'react-select';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import ResizableImage from './ResizableImage';
import { draftRawToPlainText, renderDraftContent } from '../../util/draftToHtml';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { passageToolbar, editorContainerStyles } from '../../util/editorToolbarConfig';

const BulkUploadPassagesReviewModal = ({
  isOpen,
  toggle,
  passages,
  subjects,
  units,
  parseErrors,
  onSave,
  onUpdatePassage
}) => {
  const [selectAll, setSelectAll] = useState(true);
  const [selectedPassages, setSelectedPassages] = useState(
    passages.reduce((acc, _, i) => ({ ...acc, [i]: true }), {})
  );
  const [editingIndex, setEditingIndex] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [editorStates, setEditorStates] = useState({});
  const [passagesLength, setPassagesLength] = useState(0);

  useEffect(() => {
    if (passages.length !== passagesLength) {
      setSelectedPassages(passages.reduce((acc, _, i) => ({ ...acc, [i]: true }), {}));
      setSelectAll(true);
      setEditingIndex(null);
      setPassagesLength(passages.length);
      setEditorStates({});
    }
  }, [passages.length, passagesLength]);

  const createEditorState = (value) => {
    if (!value) return EditorState.createEmpty();
    if (typeof value === 'object' && value.blocks) {
      try { return EditorState.createWithContent(convertFromRaw(value)); } catch (e) { console.warn('Failed to convert Draft.js content:', e); }
    }
    if (typeof value === 'string') return EditorState.createWithContent(ContentState.createFromText(value));
    return EditorState.createEmpty();
  };

  const getDisplayText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.blocks) return draftRawToPlainText(value);
    return '';
  };

  useEffect(() => {
    if (editingIndex !== null && !editorStates[editingIndex]) {
      const passage = passages[editingIndex];
      setEditorStates(prev => ({
        ...prev,
        [editingIndex]: {
          passageText: createEditorState(passage.passageText),
        }
      }));
    }
  }, [editingIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredUnits = (subjectId) => {
    if (!subjectId) return units || [];
    return (units || []).filter(u => u.subjectId === subjectId);
  };

  const handleSelectAll = () => {
    const newValue = !selectAll;
    setSelectAll(newValue);
    setSelectedPassages(passages.reduce((acc, _, i) => ({ ...acc, [i]: newValue }), {}));
  };

  const handleSelect = (index) => {
    const newSelected = { ...selectedPassages, [index]: !selectedPassages[index] };
    setSelectedPassages(newSelected);
    setSelectAll(Object.values(newSelected).every(v => v));
  };

  const toggleEdit = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else {
      setEditingIndex(index);
    }
  };

  const hasContent = (value) => {
    if (!value) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'object' && value.blocks) {
      return value.blocks.some(b => b.text.trim().length > 0) ||
             Object.keys(value.entityMap || {}).length > 0;
    }
    return false;
  };

  const handleFieldChange = (index, field, value) => {
    const passage = passages[index];
    const updated = { ...passage };

    if (field === 'subjectId') {
      updated.subjectId = value;
      const subject = subjects.find(s => s.subjectId === value);
      updated.subjectName = subject?.name || '';
      updated.unitId = null;
      updated.unitName = '';
    } else if (field === 'unitId') {
      updated.unitId = value;
      const unit = (units || []).find(u => u.unitId === value);
      updated.unitName = unit?.name || '';
    } else if (field === 'passageTitle') {
      updated.passageTitle = value;
    }

    validateAndUpdatePassage(index, updated);
  };

  const validateAndUpdatePassage = (index, updated) => {
    const errors = [];
    if (!updated.subjectId) errors.push('Subject is required');
    if (!updated.unitId) errors.push('Unit is required');
    if (!updated.passageTitle?.trim()) errors.push('Passage Title is required');
    if (!hasContent(updated.passageText)) errors.push('Passage Text is required');
    updated.hasValidationErrors = errors.length > 0;
    updated.validationErrors = errors;

    onUpdatePassage(index, updated);
  };

  const handleEditorChange = (index, field, editorState) => {
    setEditorStates(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: editorState }
    }));

    const raw = convertToRaw(editorState.getCurrentContent());

    onUpdatePassage(index, (prevPassage) => {
      const updated = { ...prevPassage };

      if (field === 'passageText') {
        updated.passageText = raw;
      }

      const errors = [];
      if (!updated.subjectId) errors.push('Subject is required');
      if (!updated.unitId) errors.push('Unit is required');
      if (!updated.passageTitle?.trim()) errors.push('Passage Title is required');
      if (!hasContent(updated.passageText)) errors.push('Passage Text is required');
      updated.hasValidationErrors = errors.length > 0;
      updated.validationErrors = errors;

      return updated;
    });
  };

  const editorStatesRef = useRef(editorStates);
  editorStatesRef.current = editorStates;
  const handleEditorChangeRef = useRef(handleEditorChange);
  handleEditorChangeRef.current = handleEditorChange;

  const createBlockRenderer = useCallback((passageIndex, field) => (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ResizableImage,
        editable: false,
        props: {
          onResize: (entityKey, newWidth) => {
            const currentEditorState = editorStatesRef.current[passageIndex]?.[field];
            if (!currentEditorState) return;
            const contentState = currentEditorState.getCurrentContent();
            const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
            const newEditorState = EditorState.push(currentEditorState, newContentState, 'apply-entity');
            handleEditorChangeRef.current(passageIndex, field, newEditorState);
          }
        }
      };
    }
    return undefined;
  }, []);

  const renderContent = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
    if (typeof value === 'object' && value.blocks) return renderDraftContent(value);
    return null;
  };

  const handleAddTag = () => {
    if (editingIndex === null || !tagInput.trim()) return;
    const newTags = tagInput.split(',').map(t => t.trim()).filter(t => t);
    const passage = passages[editingIndex];
    const existingTags = passage.tags || [];
    const uniqueNewTags = newTags.filter(t => !existingTags.includes(t));

    if (uniqueNewTags.length > 0) {
      const updated = { ...passage, tags: [...existingTags, ...uniqueNewTags] };
      validateAndUpdatePassage(editingIndex, updated);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    if (editingIndex === null) return;
    const passage = passages[editingIndex];
    const updated = {
      ...passage,
      tags: (passage.tags || []).filter(t => t !== tag)
    };
    validateAndUpdatePassage(editingIndex, updated);
  };

  const totalCount = passages.length;
  const validCount = passages.filter(p => !p.hasValidationErrors && p.subjectId && p.unitId).length;
  const errorCount = passages.filter(p => p.hasValidationErrors).length;
  const selectedCount = Object.values(selectedPassages).filter(v => v).length;

  const passagesToSave = passages.filter((p, i) =>
    selectedPassages[i] && !p.hasValidationErrors && p.subjectId && p.unitId
  );

  const handleSave = () => {
    if (passagesToSave.length > 0) {
      onSave(passagesToSave);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" keyboard={false} style={{ maxWidth: '95%' }}>
      <ModalHeader toggle={toggle}>
        <i className="fas fa-book-open mr-2 text-primary"></i>
        Review Passages Before Upload
        <Badge color="info" className="ml-3">{passages.length} passages</Badge>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <style>{editorContainerStyles}</style>
        {/* Summary Stats */}
        <Row className="mb-3">
          <Col md="3">
            <Alert color="info" className="mb-0 py-2">
              <i className="fas fa-list mr-2"></i>
              Total: <strong>{totalCount}</strong>
            </Alert>
          </Col>
          <Col md="3">
            <Alert color="success" className="mb-0 py-2">
              <i className="fas fa-check mr-2"></i>
              Valid: <strong>{validCount}</strong>
            </Alert>
          </Col>
          <Col md="3">
            <Alert color="warning" className="mb-0 py-2">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Errors: <strong>{errorCount}</strong>
            </Alert>
          </Col>
          <Col md="3">
            <Alert color="primary" className="mb-0 py-2">
              <i className="fas fa-check-square mr-2"></i>
              Selected: <strong>{selectedCount}</strong>
            </Alert>
          </Col>
        </Row>

        {/* Parse Errors */}
        {parseErrors && parseErrors.length > 0 && (
          <Alert color="danger" className="mb-3">
            <h5 className="alert-heading">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Parse Errors:
            </h5>
            <ul className="mb-0">
              {parseErrors.map((err, i) => (
                <li key={i}>
                  {typeof err === 'string' ? err : `Row ${err.row}: ${err.errors?.join(', ')}`}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Select All / Header Actions */}
        <Card className="mb-3 bg-lighter">
          <CardBody className="py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="mr-2"
                  style={{ marginLeft: 0, position: 'relative' }}
                />
                <span className="font-weight-bold">
                  {selectAll ? 'Deselect All' : 'Select All'}
                </span>
              </div>
              <div className="text-muted">
                <i className="fas fa-info-circle mr-1"></i>
                Click <i className="fas fa-edit"></i> to edit a passage with rich text editor
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Passage Cards */}
        {passages.map((passage, index) => {
          const isSelected = selectedPassages[index] || false;
          const isEditing = editingIndex === index;
          const hasErrors = passage.hasValidationErrors;
          const isValid = !hasErrors && passage.subjectId && passage.unitId;
          const filteredUnits = getFilteredUnits(passage.subjectId);

          return (
            <Card
              key={index}
              className={`mb-3 ${hasErrors ? 'border-danger' : isValid ? 'border-success' : 'border-warning'}`}
              style={{ borderWidth: '2px' }}
            >
              <CardHeader className={`py-2 ${hasErrors ? 'bg-danger text-white' : isValid ? 'bg-success text-white' : 'bg-warning'}`}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(index)}
                      className="mr-3"
                      style={{ marginLeft: 0, position: 'relative' }}
                    />
                    <Badge color="light" className="mr-2 text-dark">Row {passage.rowNumber}</Badge>
                    {passage.subjectName && (
                      <Badge color="primary" className="mr-2">{passage.subjectName}</Badge>
                    )}
                    {passage.unitName && (
                      <Badge color="info" className="mr-2">{passage.unitName}</Badge>
                    )}
                    {passage.passageTitle && (
                      <Badge color="light" className="mr-2 text-dark">
                        <i className="fas fa-book-open mr-1"></i>{passage.passageTitle}
                      </Badge>
                    )}
                  </div>
                  <div>
                    {hasErrors && (
                      <Badge color="light" className="mr-2 text-danger">
                        <i className="fas fa-times mr-1"></i>
                        {passage.validationErrors.length} Error{passage.validationErrors.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                    <Button
                      color={isEditing ? 'light' : 'warning'}
                      size="sm"
                      onClick={() => toggleEdit(index)}
                      className="ml-2"
                    >
                      <i className={`fas fa-${isEditing ? 'times' : 'edit'} mr-1`}></i>
                      {isEditing ? 'Close' : 'Edit'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {hasErrors && !isEditing && (
                  <Alert color="danger" className="mb-3 py-2">
                    <strong>Errors:</strong> {passage.validationErrors.join(', ')}
                  </Alert>
                )}

                <Collapse isOpen={isEditing}>
                  <Card className="mb-3 bg-lighter">
                    <CardBody>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Subject <span className="text-danger">*</span>
                            </Label>
                            <Select
                              options={subjects.map(subject => ({
                                value: subject.subjectId,
                                label: subject.code ? `${subject.code} ${subject.name}` : subject.name
                              }))}
                              value={
                                passage.subjectId
                                  ? {
                                      value: passage.subjectId,
                                      label: (() => {
                                        const s = subjects.find(s => s.subjectId === passage.subjectId);
                                        return s?.code ? `${s.code} ${s.name}` : s?.name;
                                      })()
                                    }
                                  : null
                              }
                              onChange={(option) => handleFieldChange(index, 'subjectId', option?.value || '')}
                              styles={customDropdownWithBorder}
                              placeholder="Select a subject"
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Unit <span className="text-danger">*</span>
                            </Label>
                            <Select
                              options={filteredUnits.map(unit => ({
                                value: unit.unitId,
                                label: unit.code ? `${unit.code} ${unit.name}` : unit.name
                              }))}
                              value={
                                passage.unitId
                                  ? {
                                      value: passage.unitId,
                                      label: (() => {
                                        const u = filteredUnits.find(u => u.unitId === passage.unitId);
                                        return u?.code ? `${u.code} ${u.name}` : u?.name;
                                      })()
                                    }
                                  : null
                              }
                              onChange={(option) => handleFieldChange(index, 'unitId', option?.value || '')}
                              styles={customDropdownWithBorder}
                              placeholder="Select a unit"
                              isDisabled={!passage.subjectId}
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Passage Title <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="text"
                              value={passage.passageTitle || ''}
                              onChange={(e) => handleFieldChange(index, 'passageTitle', e.target.value)}
                              placeholder="Enter passage title"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup>
                        <Label className="form-control-label d-flex align-items-center">
                          Passage Text <span className="text-danger ml-1">*</span>
                        </Label>
                        {editorStates[index] && (
                          <div className="border rounded p-2 passage-editor-container">
                            <Editor
                              editorState={editorStates[index].passageText}
                              onEditorStateChange={(state) => handleEditorChange(index, 'passageText', state)}
                              toolbar={passageToolbar}
                              customBlockRenderFunc={createBlockRenderer(index, 'passageText')}
                            />
                          </div>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label className="form-control-label">Tags (Optional)</Label>
                        <div className="d-flex mb-2">
                          <Input
                            type="text"
                            placeholder="Add tags (separate with commas)"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); }
                            }}
                          />
                          <Button color="primary" className="ml-2" onClick={handleAddTag} style={{ whiteSpace: 'nowrap' }}>
                            <i className="fas fa-plus mr-2"></i>Add
                          </Button>
                        </div>
                        <div>
                          {(passage.tags || []).map((tag, tagIndex) => (
                            <Badge key={tagIndex} color="info" className="mr-2 mb-2" style={{ fontSize: '14px' }}>
                              {tag}
                              <Button close size="sm" className="ml-2" onClick={() => handleRemoveTag(tag)} />
                            </Badge>
                          ))}
                        </div>
                      </FormGroup>

                      {hasErrors && (
                        <Alert color="danger" className="mb-0">
                          <strong>Remaining Errors:</strong> {passage.validationErrors.join(', ')}
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                </Collapse>

                {!isEditing && (
                  <>
                    <div className="mb-3">
                      <h5 className="mb-2 text-muted"><i className="fas fa-heading mr-2"></i>Title:</h5>
                      <p className="mb-0 font-weight-bold">
                        {passage.passageTitle || <span className="text-danger font-italic">Missing title</span>}
                      </p>
                    </div>

                    <div className="mb-3">
                      <h5 className="mb-2 text-muted"><i className="fas fa-book-open mr-2"></i>Passage Text:</h5>
                      <div className="mb-0">
                        {renderContent(passage.passageText) || <span className="text-danger font-italic">Missing passage text</span>}
                      </div>
                    </div>

                    {passage.tags && passage.tags.length > 0 && (
                      <div>
                        <h5 className="mb-2 text-muted"><i className="fas fa-tags mr-2"></i>Tags:</h5>
                        <div>
                          {passage.tags.map((tag, tagIndex) => (
                            <Badge key={tagIndex} color="info" className="mr-2">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          );
        })}
      </ModalBody>
      <ModalFooter className="bg-lighter">
        <div className="d-flex w-100 justify-content-between align-items-center">
          <div>
            <span className="text-muted mr-3">
              <i className="fas fa-check-square mr-1"></i>{selectedCount} selected
            </span>
            <span className={passagesToSave.length > 0 ? 'text-success font-weight-bold' : 'text-muted'}>
              <i className="fas fa-upload mr-1"></i>{passagesToSave.length} ready to upload
            </span>
          </div>
          <div>
            <Button color="secondary" onClick={toggle} className="mr-2">
              <i className="fas fa-times mr-2"></i>Cancel
            </Button>
            <Button color="success" onClick={handleSave} disabled={passagesToSave.length === 0}>
              <i className="fas fa-upload mr-2"></i>Upload {passagesToSave.length} Passages
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default BulkUploadPassagesReviewModal;
